import type {CSSProperties} from 'react';
import {T} from '@territory/tokens';
import {radius} from '../scale.js';

export type WedgeBv2FeedbackLook = 'bv2tfb-row' | 'bv2tfb-row-on' | 'bv2tfb-stack' | 'bv2tfb-stack-on';

const BV2_FEEDBACK_LOOKS = new Set<string>(['bv2tfb-row', 'bv2tfb-row-on', 'bv2tfb-stack', 'bv2tfb-stack-on']);
export const isWedgeBv2FeedbackLook = (look: string | undefined): look is WedgeBv2FeedbackLook => look !== undefined && BV2_FEEDBACK_LOOKS.has(look);

/* Released brief v2 feedback row (prep-brief-v2-tabs, Bv2tFeedbackCard): a full box row with a 14px radius that carries a label and a
   sub label. The row look is 48px high with 0 16px padding, a flex row, labels at the two ends and the centered cross axis. The stack
   look (tablet and phone widths) is 60px high with 10px 16px padding, a flex column of 2px gap, left aligned and centered. The -on looks
   are the picked row: a 2px border where the others have a 1px border. The fill, the edge color and the text ink come from the tab
   palette, so the caller sets them as the --wedge-bv2t-fb-fill, --wedge-bv2t-fb-edge and --wedge-bv2t-fb-ink roles; the caller also sets
   the font family. While busy the row keeps the default cursor. The look draws no transparent target and no anchor
   (wedgeBv2FeedbackDrawsHit): the row is 48px or 60px high and spans the card. */
const role = (name: string, fallback: string): string => `var(--wedge-bv2t-fb-${name}, ${fallback})`;
export const wedgeBv2FeedbackDrawsHit = (look: string): boolean => !isWedgeBv2FeedbackLook(look);

export function wedgeBv2FeedbackStyle(look: WedgeBv2FeedbackLook, disabled = false): CSSProperties {
  const stack = look === 'bv2tfb-stack' || look === 'bv2tfb-stack-on';
  const on = look === 'bv2tfb-row-on' || look === 'bv2tfb-stack-on';
  return {
    minHeight: stack ? 60 : 48, padding: stack ? '10px 16px' : '0 16px', borderRadius: radius.xl,
    display: 'flex', flexDirection: stack ? 'column' : 'row', gap: stack ? 2 : 0,
    alignItems: stack ? 'flex-start' : 'center', justifyContent: stack ? 'center' : 'space-between',
    cursor: disabled ? 'default' : 'pointer',
    background: role('fill', T.surfaceAlt), border: `${on ? 2 : 1}px solid ${role('edge', T.separator)}`,
    color: role('ink', T.label), textAlign: 'left', boxSizing: 'border-box',
  };
}
